/**
 * Table state ↔ URL (T11-05): `?filter[status]=active&filter[status]=draft&sort=price_desc&view=v1
 * &limit=50&cursor=…` — a copied link opens the same filters/sort/view. The same `filter[…]`
 * names go to the API.
 */
import { type FilterState, PAGE_SIZES, type PageSize } from "./types";

export interface TableUrlState {
  filters: FilterState;
  sort: string | null;
  view: string | null;
  limit: PageSize;
  cursor: string | null;
}

const FILTER_KEY = /^filter\[([^\]]+)\]$/;

export function parseTableParams(
  params: URLSearchParams,
  defaultLimit: PageSize = 25,
): TableUrlState {
  const filters: Record<string, string | string[]> = {};
  for (const [key, value] of params.entries()) {
    const match = FILTER_KEY.exec(key);
    if (!match?.[1] || value === "") {
      continue;
    }
    const id = match[1];
    const existing = filters[id];
    filters[id] =
      existing === undefined
        ? value
        : [...(Array.isArray(existing) ? existing : [existing]), value];
  }
  const limitRaw = Number(params.get("limit"));
  const limit = (PAGE_SIZES as readonly number[]).includes(limitRaw)
    ? (limitRaw as PageSize)
    : defaultLimit;
  return {
    filters,
    sort: params.get("sort") || null,
    view: params.get("view") || null,
    limit,
    cursor: params.get("cursor") || null,
  };
}

/** Writes the table keys into a copy of `base` (other params of the page are kept). */
export function writeTableParams(
  state: TableUrlState,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const out = new URLSearchParams();
  for (const [key, value] of base.entries()) {
    if (!FILTER_KEY.test(key) && !["sort", "view", "limit", "cursor"].includes(key)) {
      out.append(key, value);
    }
  }
  for (const id of Object.keys(state.filters).sort()) {
    const value = state.filters[id];
    for (const v of Array.isArray(value) ? value : [value]) {
      if (v !== undefined && v !== "") {
        out.append(`filter[${id}]`, v);
      }
    }
  }
  if (state.sort) {
    out.set("sort", state.sort);
  }
  if (state.view) {
    out.set("view", state.view);
  }
  if (state.limit !== 25) {
    out.set("limit", String(state.limit));
  }
  if (state.cursor) {
    out.set("cursor", state.cursor);
  }
  return out;
}

/** Query string for the list endpoint: `limit`, `cursor`, `sort`, `filter[…]`, `fields`. */
export function apiQuery(
  request: {
    limit: number;
    cursor: string | null;
    sort: string | null;
    filters: FilterState;
    fields?: readonly string[];
  },
  withFields = false,
): URLSearchParams {
  const params = writeTableParams({
    filters: request.filters,
    sort: request.sort,
    view: null,
    limit: 25,
    cursor: request.cursor,
  });
  params.set("limit", String(request.limit));
  if (withFields && request.fields?.length) {
    params.set("fields", request.fields.join(","));
  }
  return params;
}

export function countFilters(filters: FilterState): number {
  return Object.values(filters).reduce((n, v) => n + (Array.isArray(v) ? v.length : v ? 1 : 0), 0);
}

export function sameFilters(a: FilterState, b: FilterState): boolean {
  const norm = (f: FilterState) =>
    JSON.stringify(
      Object.keys(f)
        .sort()
        .map((k) => {
          const v = f[k];
          return [k, Array.isArray(v) ? [...v].sort() : v];
        }),
    );
  return norm(a) === norm(b);
}
